<script setup>
// Шапка чату: масштаб, сповіщення, закриття — і під нею перемикач гілок.
// Одна на два місця: плаваюче вікно і панель кінотеатру в плеєрі.
// Прозорість є тільки у вікна, тому вона за прапорцем.
//
// Атрибути (зокрема обробники перетягування вікна) навмисно вішаємо на верхній
// рядок, а не на корінь — тягати чат за перемикач гілок не треба.
defineOptions({ inheritAttrs: false })

const props = defineProps({
  zoom: { type: Number, default: 1 },
  showOpacity: { type: Boolean, default: false },
  opacity: { type: Number, default: 1 },
  showClose: { type: Boolean, default: true },
  // Верхній рядок вікна ще й ручка перетягування
  draggable: { type: Boolean, default: false }
})

const emit = defineEmits(['update:zoom', 'update:opacity', 'close'])

const MIN_ZOOM = 0.5
const MAX_ZOOM = 2
const ZOOM_STEP = 0.1

const zoomIn = () =>
  emit(
    'update:zoom',
    Math.min(MAX_ZOOM, Math.round((props.zoom + ZOOM_STEP) * 100) / 100)
  )

const zoomOut = () =>
  emit(
    'update:zoom',
    Math.max(MIN_ZOOM, Math.round((props.zoom - ZOOM_STEP) * 100) / 100)
  )

const onOpacityInput = (e) => emit('update:opacity', Number(e.target.value) / 100)

const { scopes, activeScope, hasChoice, selectScope } = useSiteChat()

const {
  settings: notifySettings,
  toggleSetting: toggleNotifySetting
} = useChatNotifications()

const settingsOpen = ref(false)
</script>

<template>
  <div class="flex flex-col flex-shrink-0">
    <!-- Керування -->
    <div
      v-bind="$attrs"
      class="flex items-center gap-2 bg-base-200 px-2 py-1.5"
      :class="{ 'cursor-move': draggable }"
    >
      <button
        @click="zoomOut"
        @mousedown.stop
        @touchstart.stop
        class="btn btn-ghost btn-xs btn-circle shrink-0"
        title="Зменшити масштаб"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <line
            x1="5"
            y1="12"
            x2="19"
            y2="12"
          ></line>
        </svg>
      </button>

      <span class="opacity-70 w-8 text-[10px] text-center shrink-0">
        {{ Math.round(zoom * 100) }}%
      </span>

      <button
        @click="zoomIn"
        @mousedown.stop
        @touchstart.stop
        class="btn btn-ghost btn-xs btn-circle shrink-0"
        title="Збільшити масштаб"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <line
            x1="12"
            y1="5"
            x2="12"
            y2="19"
          ></line>
          <line
            x1="5"
            y1="12"
            x2="19"
            y2="12"
          ></line>
        </svg>
      </button>

      <div class="flex-1"></div>

      <template v-if="showOpacity">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          class="opacity-70 shrink-0"
        >
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
          <circle
            cx="12"
            cy="12"
            r="3"
          />
        </svg>
        <input
          type="range"
          min="20"
          max="100"
          step="5"
          :value="opacity * 100"
          @input="onOpacityInput"
          @mousedown.stop
          @touchstart.stop
          class="w-16 range range-primary range-xs shrink-0"
          title="Прозорість чату"
        />
      </template>

      <button
        @click="settingsOpen = !settingsOpen"
        @mousedown.stop
        @touchstart.stop
        class="btn btn-ghost btn-xs btn-circle shrink-0"
        :class="{ 'text-primary': settingsOpen }"
        title="Сповіщення"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="15"
          height="15"
          viewBox="0 0 24 24"
        >
          <path
            fill="currentColor"
            d="M12 22q-.825 0-1.412-.587T10 20h4q0 .825-.587 1.413T12 22m-8-3v-2h2v-7q0-2.075 1.25-3.687T10.5 4.2v-.7q0-.625.438-1.062T12 2t1.063.438T13.5 3.5v.7q2 .5 3.25 2.113T18 10v7h2v2z"
          />
        </svg>
      </button>

      <button
        v-if="showClose"
        @click="emit('close')"
        @mousedown.stop
        @touchstart.stop
        class="btn btn-ghost btn-xs btn-circle shrink-0"
        title="Закрити чат"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <line
            x1="18"
            y1="6"
            x2="6"
            y2="18"
          ></line>
          <line
            x1="6"
            y1="6"
            x2="18"
            y2="18"
          ></line>
        </svg>
      </button>
    </div>

    <!-- Налаштування сповіщень -->
    <div
      v-if="settingsOpen"
      class="flex flex-col gap-1.5 bg-base-200 px-3 pt-1 pb-2 border-base-content/10 border-b"
      @mousedown.stop
      @touchstart.stop
    >
      <label class="flex justify-between items-center gap-2 cursor-pointer">
        <span class="font-medium text-xs">Показувати сповіщення</span>
        <input
          type="checkbox"
          class="toggle toggle-primary toggle-xs"
          :checked="notifySettings.enabled"
          @change="toggleNotifySetting('enabled')"
        />
      </label>

      <label
        v-for="src in ['twitch', 'site', 'anime', 'episode']"
        :key="src"
        class="flex justify-between items-center gap-2 cursor-pointer"
        :class="{ 'opacity-40 pointer-events-none': !notifySettings.enabled }"
      >
        <span class="text-[11px] text-base-content/70">{{
          SOURCE_LABELS[src]
        }}</span>
        <input
          type="checkbox"
          class="toggle toggle-primary toggle-xs"
          :checked="notifySettings[src]"
          @change="toggleNotifySetting(src)"
        />
      </label>
    </div>

    <!-- Перемикач гілок — тільки там, де є з чого обирати (/watch/) -->
    <div
      v-if="hasChoice"
      class="flex gap-1 bg-base-200 px-2 pb-1.5"
      @mousedown.stop
      @touchstart.stop
    >
      <button
        v-for="s in scopes"
        :key="s.id"
        @click="selectScope(s.id)"
        :title="s.hint"
        class="flex-1 px-2 py-1 rounded font-medium text-[11px] truncate transition-colors"
        :class="
          activeScope === s.id
            ? 'bg-primary text-primary-content'
            : 'bg-base-300/60 hover:bg-base-300 text-base-content/70'
        "
      >
        {{ s.short }}
      </button>
    </div>
  </div>
</template>
